/**
 * previewMappingResult.js
 *
 * Turns a matched mapping rule's raw `result` into what the designer will
 * actually see on the plate — the last step of the Preview chain
 * (Incoming → Transformed → Matched rule → Result).
 *
 * Two substitutions, both purely presentational:
 *
 *  1. The binding's incoming-value expression. A composed string result
 *     may embed real Jinja (see bindingGenerators.js's
 *     buildIncomingValueExpression — that is the "variable" the String
 *     Builder inserts, deliberately not a new token syntax). Home
 *     Assistant resolves it at runtime; here it is replaced with the
 *     preview value so `Laden {{ states("sensor.x") }} W` reads
 *     `Laden -116 W`. Only that one expression is recognised — any other
 *     Jinja in a result is left as written, since the Designer has no
 *     Home Assistant state to evaluate it against and inventing one
 *     would make the preview lie.
 *
 *  2. `\uXXXX` icon notation → the icon character, so a composed result
 *     previews as the glyph rather than an escape sequence. Icons picked
 *     through the String Builder are already stored as the character;
 *     this covers a hand-typed escape as well, exactly as
 *     iconFonts.js's processIconText() does for the canvas renderer.
 *
 * Pure — no Vue, no generator, no Home Assistant. Never used for
 * generation: the generator emits `result` verbatim.
 */
import { iconNotationToChars } from '../../utils/iconTextNotation.js';
import { jinjaExpressionPattern } from './incomingValueToken.js';

/**
 * @param {object} options
 * @param {*} options.result — the matched rule's `result`
 * @param {string} [options.valueExpression] — the binding's incoming-value
 *        Jinja expression, from buildIncomingValueExpression()
 * @param {*} [options.value] — the value to show in its place (the
 *        transformed preview value)
 * @returns {string}
 */
export function previewMappingResult({ result, valueExpression = '', value = '' } = {}) {
  let out = result === undefined || result === null ? '' : String(result);
  if (valueExpression) {
    out = out.replace(jinjaExpressionPattern(valueExpression), String(value));
  }
  return iconNotationToChars(out);
}
